import { BRAND_NAME } from "../../constants/brand";
import { useContext, useEffect, useState } from 'react';
import { Grid, Typography, TextField, Button, Box, Divider, Link } from "@mui/material";
import contactUsPageStyles from '../../Styles/ComponentStyles/LandingPage/ContactUsPage.module.scss';
import { UserContext, UserContextPayload } from '../../Context/UserDetailContext';
import EastIcon from '@mui/icons-material/East';
import ReCAPTCHA from 'react-google-recaptcha';
import { useSnackbar } from '../../GlobalComponent/GlobalSnackbar/GlobalSnackbar';
import agent from '../../service/api';
import Astrict from '../../GlobalComponent/Astrict/Astrict';

const ContactUs = () => {

    const {
        setFlagLoader
    } = useContext(UserContext) as UserContextPayload

    const { showSnackbar } = useSnackbar();

    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        message: '',
        recaptchaResponse: ''
    });

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        try {
            setFlagLoader(true);

            if (!formData.firstname || !formData.lastname || !formData.email || !formData.phone || !formData.message || !formData.recaptchaResponse) {
                showSnackbar("Please fill all required fields!", "error");
                return;
            }

            const response = await agent.postContactUsAPI.post(formData);
            if (response?.status) {
                showSnackbar("Message sent successfully!", "success");
            } else {
                showSnackbar(response?.error?.message || "Something went wrong!", "error");
            }
        } catch (error) {
            console.error("Error sending message:", error);
            showSnackbar("Something went wrong!", "error");
        } finally {
            setFlagLoader(false);
        }
    };

    return (
        <>
            <Grid container className={contactUsPageStyles.contactUsMainContainer}>
                <Grid item xs={12}>
                    <Typography className={contactUsPageStyles.pageHeader}>Contact Us</Typography>
                </Grid>
                <Grid item xs={12} className={contactUsPageStyles.pageSubHeader}>
                    Any question or remarks? Just write us a message!
                </Grid>
                <Grid item lg={9} md={10} sm={11} xs={12}>
                    <Grid container className={contactUsPageStyles.contactUsFormContainer}>
                        <Grid item xs={12} lg={5} md={6} className={contactUsPageStyles.leftSection}>
                            <Grid container>
                                <Grid item xs={12}>
                                    <Typography className={contactUsPageStyles.conversationTitle}>
                                        Let's Start a Conversation
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography className={contactUsPageStyles.conversationSubtitle}>
                                        Our team of experts is here to help you understand how {BRAND_NAME}
                                        can enhance your client relationships and grow your practice.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}>
                                    <Box className={contactUsPageStyles.whyChooseContainer}>
                                        <Typography className={contactUsPageStyles.whyChooseTitle}>
                                            Why Choose {BRAND_NAME}?
                                        </Typography>
                                        <Box className={contactUsPageStyles.benefitsList}>
                                            <Box className={contactUsPageStyles.benefitItem}>
                                                <span className={contactUsPageStyles.benefitBullet}></span>
                                                <Typography className={contactUsPageStyles.benefitText}>Enhance client engagement</Typography>
                                            </Box>
                                            <Box className={contactUsPageStyles.benefitItem}>
                                                <span className={contactUsPageStyles.benefitBullet}></span>
                                                <Typography className={contactUsPageStyles.benefitText}>Attract new customers</Typography>
                                            </Box>
                                            <Box className={contactUsPageStyles.benefitItem}>
                                                <span className={contactUsPageStyles.benefitBullet}></span>
                                                <Typography className={contactUsPageStyles.benefitText}>Deliver tailored solutions</Typography>
                                            </Box>
                                            <Box className={contactUsPageStyles.benefitItem}>
                                                <span className={contactUsPageStyles.benefitBullet}></span>
                                                <Typography className={contactUsPageStyles.benefitText}>Industry-leading technology</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} lg={7} md={6} className={contactUsPageStyles.rightSection}>
                            <Grid container>
                                <Grid item xs={12}>
                                    <Typography className={contactUsPageStyles.formTitle}>
                                        Send us a Message
                                    </Typography>
                                </Grid>

                                {/* Form Fields Row 1: First Name & Last Name */}
                                <Grid item xs={12}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography className={contactUsPageStyles.fieldLabel}>
                                                First Name <Astrict />
                                            </Typography>
                                            <TextField
                                                name="firstname"
                                                placeholder="Enter First Name"
                                                value={formData.firstname}
                                                onChange={handleInputChange}
                                                fullWidth
                                                variant="outlined"
                                                className={contactUsPageStyles.textField}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography className={contactUsPageStyles.fieldLabel}>
                                                Last Name <Astrict />
                                            </Typography>
                                            <TextField
                                                name="lastname"
                                                placeholder="Enter Last Name"
                                                value={formData.lastname}
                                                onChange={handleInputChange}
                                                fullWidth
                                                variant="outlined"
                                                className={contactUsPageStyles.textField}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* Email Field */}
                                <Grid item xs={12}>
                                    <Typography className={contactUsPageStyles.fieldLabel}>
                                        Email <Astrict />
                                    </Typography>
                                    <TextField
                                        name="email"
                                        type="email"
                                        placeholder="Enter Email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        fullWidth
                                        variant="outlined"
                                        className={contactUsPageStyles.textField}
                                    />
                                </Grid>

                                {/* Phone Field */}
                                <Grid item xs={12}>
                                    <Typography className={contactUsPageStyles.fieldLabel}>
                                        Phone <Astrict />
                                    </Typography>
                                    <TextField
                                        name="phone"
                                        type="tel"
                                        placeholder="Enter Phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        fullWidth
                                        variant="outlined"
                                        className={contactUsPageStyles.textField}
                                    />
                                </Grid>

                                {/* Message Field */}
                                <Grid item xs={12}>
                                    <Typography className={contactUsPageStyles.fieldLabel}>
                                        Message <Astrict />
                                    </Typography>
                                    <TextField
                                        name="message"
                                        placeholder="Enter Message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        fullWidth
                                        multiline
                                        rows={4}
                                        variant="outlined"
                                        className={contactUsPageStyles.textField}
                                    />
                                </Grid>

                                {/* captcha */}
                                <Grid item xs={12}>
                                    <ReCAPTCHA
                                        sitekey="6LftXpkqAAAAAOHtYQv1WJt_7Kq9m1asUWeeAahO"
                                        onChange={(token: any) => {
                                            setFormData({
                                                ...formData,
                                                recaptchaResponse: token
                                            })
                                        }}
                                    />
                                </Grid>

                                {/* Submit Button */}
                                <Grid item xs={12}>
                                    <Button
                                        onClick={handleSubmit}
                                        fullWidth
                                        className={contactUsPageStyles.submitButton}
                                        endIcon={<EastIcon fontSize='medium' />}
                                    >
                                        Send Message
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

            </Grid>
        </>
    )
}

export default ContactUs;